// 자산 묶음(지역별 데이터 묶음): public/assets/** 의 모든 파일을 한 묶음에만 넣는 규칙. 순수 모듈(빌드·서비스 워커·게임·시험이 같이 쓴다).
//
// 첫 진입(first=true): 타이틀·마을①·지역1·공용(영웅 T1·UI 뼈대·기본 오디오·VFX). 이것만 첫 접속에 받는 게 목표다.
// 지역 묶음(r2·r3·r4)·부가 묶음: 첫 진입 묶음 뒤에 이어서 모두 받는다(마을·타이틀·결과 화면에서, regionData.ts). 들어가기 직전이면 그 지역을 먼저 서두른다.
// 새 폴더·파일을 public/assets 에 추가하면 아래 규칙에 한 줄을 더해야 한다 — bundles.test.ts 가 "묶음 없는 파일"을 실패로 잡는다.

export const BUNDLE_IDS = ['core', 'village', 'r1', 'r2', 'r3', 'r4', 'r5', 'r6', 'towns', 'hero', 'ui'] as const;
export type BundleId = (typeof BUNDLE_IDS)[number];
export type RegionId = 'r1' | 'r2' | 'r3' | 'r4' | 'r5' | 'r6';

export interface BundleInfo {
  label: string;
  /** 이 묶음이 속한 지역(없으면 null) */
  region: RegionId | null;
  /** 첫 접속에 받는 묶음 */
  first: boolean;
}

export const BUNDLES: Readonly<Record<BundleId, BundleInfo>> = {
  core: { label: '공용', region: null, first: true },
  village: { label: '마을', region: null, first: true },
  r1: { label: '지역1', region: 'r1', first: true },
  r2: { label: '지역2', region: 'r2', first: false },
  r3: { label: '지역3', region: 'r3', first: false },
  r4: { label: '지역4', region: 'r4', first: false },
  r5: { label: '지역5', region: 'r5', first: false },
  r6: { label: '지역6', region: 'r6', first: false },
  towns: { label: '마을②~⑥', region: null, first: false },
  hero: { label: '영웅 단계', region: null, first: false },
  ui: { label: '화면 그림', region: null, first: false },
};

/** 규칙은 위에서부터 처음 맞는 것. 경로는 assets/ 아래 상대 경로 */
const RULES: ReadonlyArray<readonly [RegExp, BundleId]> = [
  [/^r1\//, 'r1'],
  [/^monsters\/mob_mushroom_shaman\.glb$/, 'r1'], // 버섯 주술사는 지역1 몹(폴더만 monsters/)
  [/^(?:r2|monsters|bosses)\//, 'r2'],
  [/^ui\/stage\/r2-/, 'r2'],
  [/^r3\//, 'r3'],
  [/^regions\/[^/]+__frost\.glb$/, 'r3'],
  [/^ui\/stage\/r3-/, 'r3'],
  [/^r4\//, 'r4'],
  [/^regions\/[^/]+__poison\.glb$/, 'r4'],
  [/^ui\/stage\/r4-/, 'r4'],
  [/^r5\//, 'r5'],
  [/^regions\/[^/]+__sand\.glb$/, 'r5'],
  [/^ui\/stage\/r5-/, 'r5'],
  [/^r6\//, 'r6'],
  [/^regions\/[^/]+__void\.glb$/, 'r6'],
  [/^ui\/stage\/r6-/, 'r6'],
  [/^ui\/stage\/r1-/, 'r1'],
  [/^village\//, 'village'],
  [/^audio\/bgm\/[^/]+-r5\.mp3$/, 'r5'], // 지역5 던전·보스·엔딩 곡
  [/^audio\/bgm\/[^/]+-r6\.mp3$/, 'r6'],
  [/^audio\/bgm\/village\.mp3$/, 'village'],
  [/^audio\/bgm\//, 'r1'],
  [/^town\//, 'towns'],
  [/^(?:characters|weapons)\/[^/]+_t[23]\.glb$/, 'hero'],
  [/^characters\/[^/]+_t[23]\.dyemask\.webp$/, 'hero'], // 염색 부위 마스크(몸 GLB와 같은 묶음)
  [/^ui\/hud\/portrait_t[23]\.webp$/, 'hero'],
  [/^ui\/kael_standee_t[23]\.webp$/, 'hero'],
  // ui/skills(2026-10-27 스킬 아이콘 104·전직 문장 8): 정비 서랍 도감·영웅 선택에서 쓴다(전투 버튼은 글리프로 대체 가능)
  [/^ui\/(?:items|skills|skilltree|daily|npc|rewards|raid|char|avatars|live)\//, 'ui'],
  [/^ui\/kael_standee\.webp$/, 'ui'],
  // 공용
  [/^ui\/skill-hud-v2\//, 'core'], // 전투와 정비가 공유하는 작은 원화: 첫 플레이 전에 준비한다.
  [/^(?:characters|weapons)\/[^/]+_t1\.glb$/, 'core'],
  [/^characters\/[^/]+_t1\.dyemask\.webp$/, 'core'],
  // 카엘 천공 올려베기 전용 클립(골격·클립만, 몸을 붙인 뒤 바로 읽는다)
  [/^characters\/kael_launcher\.glb$/, 'core'],
  [/^ui\/(?:title|hud|frames|icons|cards)\//, 'core'],
  [/^ui\/kael_cutin\.webp$/, 'core'],
  // 이벤트 주 마을 곡 5개(약 8MB): 이벤트 주 마을에서만 쓰므로 첫 접속에 받지 않는다(없으면 재생기가 마을 곡으로 대체)
  [/^audio\/live\//, 'towns'],
  [/^(?:vfx|props|npcs|audio)\//, 'core'],
];

/** 런타임이 읽지 않는 메모·목록 파일(묶음 없음) */
const IGNORED_RE = /(?:^|\/)(?:ui-art-manifest\.json|icons-manifest\.json|[^/]+\.(?:md|txt))$/i;

/** 묶음을 정한다. 규칙에 없으면 null (빌드가 'core' 로 두고 시험이 실패로 알린다) */
export function bundleOfStrict(rel: string): BundleId | null {
  for (const [re, id] of RULES) if (re.test(rel)) return id;
  return null;
}

export function isIgnoredAsset(rel: string): boolean { return IGNORED_RE.test(rel); }

/** 묶음(규칙에 없으면 공용) */
export function bundleOf(rel: string): BundleId { return bundleOfStrict(rel) ?? 'core'; }

/** 그 지역 입장에 필요한 묶음(지역1은 첫 진입 묶음이라 따로 받을 것이 없다) */
export function bundleForRegion(region: RegionId): BundleId { return region; }

/** 지역 순서 */
export const REGION_ORDER: readonly RegionId[] = ['r1', 'r2', 'r3', 'r4', 'r5', 'r6'];

/**
 * 미리 받기 순서 계획(2026-10-27 「처음에 전부 한 번에」): ① 첫 진입 묶음 ② 지역2~6 차례로(아직 안 열린 지역도) ③ 부가 묶음(마을②~④·화면 그림·영웅 단계).
 * 전체가 약 55MB(지역5·6 추가 뒤, BGM 6곡 약 10.5MB 포함)라 첫 접속 한 번에 모두 받고, 그 뒤로는 바뀐 파일만 받는다. 이미 받은 묶음은 건너뛴다.
 * unlocked 는 순서를 바꾸지 않는다(호출 호환용으로 남긴다).
 */
export function prefetchPlan(opts: { unlocked: readonly RegionId[]; done: ReadonlySet<BundleId> }): BundleId[] {
  const out: BundleId[] = [];
  const add = (b: BundleId): void => { if (!opts.done.has(b) && !out.includes(b)) out.push(b); };
  for (const id of BUNDLE_IDS) if (BUNDLES[id].first) add(id);
  for (let i = 1; i < REGION_ORDER.length; i++) add(bundleForRegion(REGION_ORDER[i]));
  add('towns'); add('ui'); add('hero');
  return out;
}
